import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { crm } from '../../api/crm';
import { useSales } from '../../context/SalesContext';

// What a leader can switch per executive.
const PERMISSIONS = [
  { key: 'leads', label: 'App leads' },
  { key: 'webinar_leads', label: 'Webinar leads' },
  { key: 'payment_links', label: 'Generate payment links' },
  { key: 'discounted_links', label: 'Discounted algo links' },
  { key: 'payment_panels', label: 'Failed / successful payments' },
  { key: 'callbacks_panel', label: 'Callbacks' },
  { key: 'revenue', label: 'Own revenue' },
  { key: 'otp', label: 'View customer OTP' },
  { key: 'raise_ticket', label: 'Raise support tickets' },
];

// Lead categories a leader can keep with themselves.
const HIDDEN = [
  { key: 'callbacks', label: 'Callback requests' },
  { key: 'failed_payments', label: 'Failed payments' },
  { key: 'paid_users', label: 'Paid users' },
  { key: 'renewals', label: 'Renewals (expiring in 15 days)' },
];

const ALGO_PLANS = [
  { key: 'starter', label: 'Starter (per 12 months)', platform: 1299 },
  { key: 'plus', label: 'Plus (per 14 months)', platform: 16000 },
  { key: 'elite', label: 'Elite (per 14 months)', platform: 45000 },
  { key: 'black', label: 'Black (per 14 months)', platform: 170000 },
];

const num = (v, fallback = 0) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : fallback;
};

// MemberDialog edits one person. What is editable depends on who is editing whom; the
// server enforces the same rules.
const MemberDialog = ({ member, isAdmin, isSelf, onClose, onSaved }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!member) return;
    setError('');
    setForm({
      number: member.number || '',
      whatsapp_number: member.whatsapp_number || '',
      lead_flow_weight: member.lead_flow_weight ?? 100,
      self_weight: member.self_weight ?? 100,
      // Webinar leads: blank = split the same way as app leads.
      webinar_lead_flow_weight: member.webinar_lead_flow_weight ?? '',
      webinar_self_weight: member.webinar_self_weight ?? '',
      permissions: { ...(member.permissions || {}) },
      hidden_categories: [...(member.hidden_categories || [])],
      team_discount_quota: member.team_discount_quota ?? 30,
      discount_quota: member.discount_quota ?? 0,
      min_prices: { ...(member.min_prices || {}) },
    });
  }, [member]);

  if (!member || !form) return null;
  const isLeaderRow = member.leader;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const body = {};
    // Blank webinar share = "same as app leads" (-1 clears the override on the server).
    const webinar = (v) => (v === '' || v === null || v === undefined ? -1 : num(v, 0));
    if (!isSelf) body.number = String(form.number).replace(/\D/g, '').slice(-10);
    body.whatsapp_number = String(form.whatsapp_number || '').replace(/\D/g, '').slice(-10);
    if (isLeaderRow) {
      if (isAdmin) {
        body.lead_flow_weight = num(form.lead_flow_weight, 100);
        body.team_discount_quota = num(form.team_discount_quota, 30);
        body.webinar_lead_flow_weight = webinar(form.webinar_lead_flow_weight);
      }
      body.self_weight = num(form.self_weight, 0);
      body.webinar_self_weight = webinar(form.webinar_self_weight);
      body.hidden_categories = form.hidden_categories;
    } else {
      body.lead_flow_weight = num(form.lead_flow_weight, 0);
      body.webinar_lead_flow_weight = webinar(form.webinar_lead_flow_weight);
      body.permissions = form.permissions;
      body.discount_quota = num(form.discount_quota, 0);
      const mins = {};
      ALGO_PLANS.forEach((p) => {
        const v = parseFloat(form.min_prices[p.key]);
        if (v > 0) mins[p.key] = v;
      });
      body.min_prices = mins;
    }
    setSaving(true);
    setError('');
    try {
      await crm(`owner-access/${encodeURIComponent(member.owner)}`, { method: 'PUT', body });
      onSaved(`${member.owner} updated`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>{member.owner} {isLeaderRow ? '· Team leader' : '· Executive'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {!isSelf && (
            <TextField
              label="Login number"
              value={form.number}
              onChange={(e) => set('number', e.target.value.replace(/\D/g, '').slice(-10))}
              inputProps={{ inputMode: 'numeric' }}
              size="small"
            />
          )}
          <TextField
            label="WhatsApp number shown to customers"
            value={form.whatsapp_number}
            onChange={(e) => set('whatsapp_number', e.target.value.replace(/\D/g, '').slice(-10))}
            inputProps={{ inputMode: 'numeric' }}
            size="small"
            placeholder={isLeaderRow ? 'Not set' : "Team leader's number"}
            InputLabelProps={{ shrink: true }}
            error={Boolean(form.whatsapp_number) && form.whatsapp_number.length !== 10}
            helperText={isLeaderRow
              ? "The app shows this to the leader's customers as their relationship manager. Executives without their own number show this one too."
              : "The app shows this to this executive's customers as their relationship manager. Leave blank to show the team leader's number."}
          />

          {isLeaderRow ? (
            <>
              {isAdmin && (
                <TextField
                  label="Team's share of all new leads"
                  type="number"
                  size="small"
                  value={form.lead_flow_weight}
                  onChange={(e) => set('lead_flow_weight', e.target.value)}
                  helperText="100 = an equal share with other teams; lower = fewer leads for this team"
                />
              )}
              {isAdmin && (
                <TextField
                  label="Team's share of webinar leads"
                  type="number"
                  size="small"
                  value={form.webinar_lead_flow_weight}
                  onChange={(e) => set('webinar_lead_flow_weight', e.target.value)}
                  placeholder="Same as above"
                  InputLabelProps={{ shrink: true }}
                  helperText="Leave blank to split webinar leads like all other leads. 0 = this team gets no webinar leads"
                />
              )}
              <TextField
                label="Leader's personal share of the team's leads"
                type="number"
                size="small"
                value={form.self_weight}
                onChange={(e) => set('self_weight', e.target.value)}
                helperText="Compared with each executive's share. 0 = the leader takes no new leads personally"
              />
              <TextField
                label="Leader's personal share of the team's webinar leads"
                type="number"
                size="small"
                value={form.webinar_self_weight}
                onChange={(e) => set('webinar_self_weight', e.target.value)}
                placeholder="Same as above"
                InputLabelProps={{ shrink: true }}
                helperText="Leave blank to use the same share as other leads"
              />
              {isAdmin && (
                <TextField
                  label="Team's discounted algo links per month"
                  type="number"
                  size="small"
                  value={form.team_discount_quota}
                  onChange={(e) => set('team_discount_quota', e.target.value)}
                  helperText="Paid links below the normal price. The leader splits this across the team."
                />
              )}
              <Box>
                <Typography variant="subtitle2">Leads that stay with the leader</Typography>
                <Typography variant="caption" color="text.secondary">
                  From now on, when an executive's lead enters one of these, it moves to the leader. The leader can assign it back.
                </Typography>
                <FormGroup>
                  {HIDDEN.map((h) => (
                    <FormControlLabel
                      key={h.key}
                      label={h.label}
                      control={
                        <Checkbox
                          size="small"
                          checked={form.hidden_categories.includes(h.key)}
                          onChange={(e) => set('hidden_categories', e.target.checked
                            ? [...form.hidden_categories, h.key]
                            : form.hidden_categories.filter((k) => k !== h.key))}
                        />
                      }
                    />
                  ))}
                </FormGroup>
              </Box>
            </>
          ) : (
            <>
              <TextField
                label="Share of the team's new leads"
                type="number"
                size="small"
                value={form.lead_flow_weight}
                onChange={(e) => set('lead_flow_weight', e.target.value)}
                helperText="Compared with the rest of the team (100 = equal). 0 = no new leads"
              />
              <TextField
                label="Share of the team's webinar leads"
                type="number"
                size="small"
                value={form.webinar_lead_flow_weight}
                onChange={(e) => set('webinar_lead_flow_weight', e.target.value)}
                placeholder="Same as above"
                InputLabelProps={{ shrink: true }}
                helperText="Leave blank to use the same share as other leads. Higher = more webinar leads, 0 = none"
              />
              <Box>
                <Typography variant="subtitle2">Access</Typography>
                <FormGroup>
                  {PERMISSIONS.map((p) => (
                    <FormControlLabel
                      key={p.key}
                      label={p.label}
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(form.permissions[p.key])}
                          onChange={(e) => set('permissions', { ...form.permissions, [p.key]: e.target.checked })}
                        />
                      }
                    />
                  ))}
                </FormGroup>
              </Box>
              <TextField
                label="Discounted algo links per month"
                type="number"
                size="small"
                value={form.discount_quota}
                onChange={(e) => set('discount_quota', e.target.value)}
                helperText="This executive's slice of the team quota. Whatever is not handed out stays with the leader."
              />
              <Box>
                <Typography variant="subtitle2">Minimum price for this executive</Typography>
                <Typography variant="caption" color="text.secondary">
                  Leave blank to use the platform minimum. Cannot be below it.
                </Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {ALGO_PLANS.map((p) => (
                    <TextField
                      key={p.key}
                      label={p.label}
                      type="number"
                      size="small"
                      placeholder={`Platform minimum ₹${p.platform.toLocaleString('en-IN')}`}
                      value={form.min_prices[p.key] ?? ''}
                      onChange={(e) => set('min_prices', { ...form.min_prices, [p.key]: e.target.value })}
                    />
                  ))}
                </Stack>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

const AddDialog = ({ open, isAdmin, leaders, onClose, onSaved }) => {
  const [form, setForm] = useState({ owner: '', number: '', whatsapp_number: '', parent_owner: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ owner: '', number: '', whatsapp_number: '', parent_owner: '' });
      setError('');
    }
  }, [open]);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await crm('owner-access', { method: 'POST', body: { owner: form.owner.trim(), number: form.number, parent_owner: form.parent_owner, ...(form.whatsapp_number ? { whatsapp_number: form.whatsapp_number } : {}) } });
      onSaved(`${form.owner.trim()} added`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{isAdmin ? 'Add a person' : 'Add an executive'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Name" size="small" value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} helperText="Shown as the lead owner everywhere. Must be unique." />
          <TextField
            label="Login number"
            size="small"
            value={form.number}
            onChange={(e) => setForm({ ...form, number: e.target.value.replace(/\D/g, '').slice(-10) })}
            inputProps={{ inputMode: 'numeric' }}
          />
          <TextField
            label="WhatsApp number shown to customers (optional)"
            size="small"
            value={form.whatsapp_number}
            onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value.replace(/\D/g, '').slice(-10) })}
            inputProps={{ inputMode: 'numeric' }}
            helperText="Blank for an executive = the team leader's number is shown"
          />
          {isAdmin && (
            <TextField select label="Role" size="small" value={form.parent_owner} onChange={(e) => setForm({ ...form, parent_owner: e.target.value })}>
              <MenuItem value="">Team leader (new team)</MenuItem>
              {leaders.map((l) => <MenuItem key={l.owner} value={l.owner}>Executive under {l.owner}</MenuItem>)}
            </TextField>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || !form.owner.trim() || form.number.length !== 10}>Add</Button>
      </DialogActions>
    </Dialog>
  );
};

// SupportUsers (admin only): people who log in to this dashboard without being sales
// people. They can look one number up and send requests; they own no leads, get none and
// appear in no team, so they are kept apart from the teams above.
const SupportUsers = ({ notify }) => {
  const [rows, setRows] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', number: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await crm('team/support-users');
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      setRows((prev) => prev || []);
      notify(e.message, 'error');
    }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setForm({ name: '', number: '' });
    setError('');
    setAdding(true);
  };

  const add = async () => {
    setBusy(true);
    setError('');
    try {
      await crm('team/support-users', { method: 'POST', body: { name: form.name.trim(), number: form.number } });
      setAdding(false);
      notify(`${form.name.trim()} added as a support user`);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const setActive = async (u, active) => {
    try {
      await crm(`team/support-users/${u.id}`, { method: 'PUT', body: { active } });
      notify(active ? `${u.name} can log in again` : `${u.name} deactivated`);
      load();
    } catch (e) {
      notify(e.message, 'error');
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name}? They will no longer be able to log in. Requests they sent are kept.`)) return;
    try {
      await crm(`team/support-users/${u.id}`, { method: 'DELETE' });
      notify(`${u.name} removed`);
      load();
    } catch (e) {
      notify(e.message, 'error');
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" fontWeight={700}>Support users</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="outlined" startIcon={<PersonAddIcon />} onClick={openAdd}>Add support user</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Not sales people: they search one customer number at a time (app and webinar details, read-only) and can send you requests. No lead lists, no leads assigned.
      </Typography>
      <Divider sx={{ my: 1.5 }} />
      {rows === null && <Typography variant="body2" color="text.secondary">Loading...</Typography>}
      {rows !== null && rows.length === 0 && <Typography variant="body2" color="text.secondary">No support users yet.</Typography>}
      <Stack spacing={1}>
        {(rows || []).map((u) => (
          <Box key={u.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', opacity: u.active ? 1 : 0.55 }}>
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {u.name} {!u.active && <Chip size="small" label="Deactivated" sx={{ ml: 0.5 }} />}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">{u.number}</Typography>
            </Box>
            <Button size="small" onClick={() => setActive(u, !u.active)}>{u.active ? 'Deactivate' : 'Activate'}</Button>
            <Button size="small" color="error" onClick={() => remove(u)}>Remove</Button>
          </Box>
        ))}
      </Stack>

      <Dialog open={adding} onClose={() => setAdding(false)} fullWidth maxWidth="xs">
        <DialogTitle>Add a support user</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Name" size="small" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
            <TextField
              label="Login number"
              size="small"
              value={form.number}
              onChange={(e) => setForm({ ...form, number: e.target.value.replace(/\D/g, '').slice(-10) })}
              inputProps={{ inputMode: 'numeric' }}
              helperText="Must not be an admin's or a sales person's number"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdding(false)}>Cancel</Button>
          <Button variant="contained" onClick={add} disabled={busy || !form.name.trim() || form.number.length !== 10}>Add</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

const Team = () => {
  const sales = useSales();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const me = await crm('owner-access/me');
      setTeam(me.team || []);
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const notify = useCallback((message, severity = 'success') => setSnackbar({ open: true, message, severity }), []);

  const saved = (message) => {
    setEditing(null);
    setAdding(false);
    setSnackbar({ open: true, message, severity: 'success' });
    load();
    sales.reload();
  };

  const setActive = async (member, active) => {
    if (!active && !window.confirm(`Remove ${member.owner}? They will no longer be able to log in and all their leads move to ${member.parent_owner || 'unassigned'}.`)) return;
    try {
      if (active) {
        await crm(`owner-access/${encodeURIComponent(member.owner)}`, { method: 'PUT', body: { active: true } });
      } else {
        await crm(`owner-access/${encodeURIComponent(member.owner)}`, { method: 'DELETE' });
      }
      saved(active ? `${member.owner} restored` : `${member.owner} removed; leads are moving to ${member.parent_owner || 'unassigned'}`);
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const leaders = useMemo(() => team.filter((m) => m.leader), [team]);

  // Leader's remaining discount quota = team quota - what was handed to executives.
  const leaderQuota = (leader) => {
    const given = team.filter((m) => m.parent_owner === leader.owner && m.active).reduce((s, m) => s + (m.discount_quota || 0), 0);
    return Math.max(0, (leader.team_discount_quota || 0) - given);
  };

  // Share of the team's new leads each active member gets (same split the backend uses).
  // webinar=true uses the webinar shares where set, else the general ones.
  const sharePct = (leader, member, webinar = false) => {
    const members = [leader, ...team.filter((m) => m.parent_owner === leader.owner)].filter((m) => m.active);
    const w = (m) => {
      const general = m.leader ? m.self_weight ?? 100 : m.lead_flow_weight ?? 0;
      const override = m.leader ? m.webinar_self_weight : m.webinar_lead_flow_weight;
      return Math.max(0, webinar && override !== null && override !== undefined ? override : general);
    };
    const total = members.reduce((sum, m) => sum + w(m), 0);
    if (!member.active) return 0;
    if (total === 0) return member.leader ? 100 : 0;
    return Math.round((w(member) / total) * 100);
  };
  // "40% of team leads" — plus the webinar figure when it differs.
  const shareText = (leader, member) => {
    const app = sharePct(leader, member);
    const web = sharePct(leader, member, true);
    return app === web ? `${app}% of team leads` : `${app}% of app leads · ${web}% of webinar leads`;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1 }}>Team</Typography>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setAdding(true)}>
          {sales.isAdmin ? 'Add person' : 'Add executive'}
        </Button>
      </Box>
      {loading && <Typography color="text.secondary">Loading...</Typography>}

      <Stack spacing={2}>
        {leaders.map((leader) => {
          const execs = team.filter((m) => m.parent_owner === leader.owner);
          return (
            <Paper key={leader.owner} variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" fontWeight={700}>{leader.owner}</Typography>
                <Chip size="small" color="primary" label="Team leader" />
                {!leader.active && <Chip size="small" color="default" label="Removed" />}
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" onClick={() => setEditing(leader)}>Settings</Button>
                {sales.isAdmin && (
                  <Button size="small" color={leader.active ? 'error' : 'primary'} onClick={() => setActive(leader, !leader.active)}>
                    {leader.active ? 'Remove' : 'Restore'}
                  </Button>
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {leader.number} · WhatsApp {leader.whatsapp_number || 'not set'} · Team lead flow {leader.lead_flow_weight}
                {leader.webinar_lead_flow_weight !== null && leader.webinar_lead_flow_weight !== undefined ? ` (webinar ${leader.webinar_lead_flow_weight})` : ''} · Personal share {shareText(leader, leader)} ·
                Discount links {leaderQuota(leader)} kept of {leader.team_discount_quota}/month
              </Typography>
              {(leader.hidden_categories || []).length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Stays with leader:</Typography>
                  {leader.hidden_categories.map((k) => <Chip key={k} size="small" variant="outlined" label={(HIDDEN.find((h) => h.key === k) || {}).label || k} />)}
                </Box>
              )}
              <Divider sx={{ my: 1.5 }} />
              {execs.length === 0 && <Typography variant="body2" color="text.secondary">No executives yet.</Typography>}
              <Stack spacing={1}>
                {execs.map((m) => (
                  <Box key={m.owner} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', opacity: m.active ? 1 : 0.55 }}>
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {m.owner} {!m.active && <Chip size="small" label="Removed" sx={{ ml: 0.5 }} />}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" component="div">
                        {m.number} · WhatsApp {m.whatsapp_number || (leader.whatsapp_number ? `${leader.whatsapp_number} (leader's)` : 'not set')} · {shareText(leader, m)} · {m.discount_quota || 0} discount links/month
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                        {PERMISSIONS.filter((p) => m.permissions?.[p.key]).map((p) => (
                          <Chip key={p.key} size="small" variant="outlined" label={p.label} sx={{ height: 20, fontSize: '0.7rem' }} />
                        ))}
                      </Box>
                    </Box>
                    {m.active && <Button size="small" onClick={() => setEditing(m)}>Edit</Button>}
                    <Button size="small" color={m.active ? 'error' : 'primary'} onClick={() => setActive(m, !m.active)}>
                      {m.active ? 'Remove' : 'Restore'}
                    </Button>
                  </Box>
                ))}
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      {sales.isAdmin && <SupportUsers notify={notify} />}

      <MemberDialog
        member={editing}
        isAdmin={sales.isAdmin}
        isSelf={Boolean(editing && editing.owner === sales.owner)}
        onClose={() => setEditing(null)}
        onSaved={saved}
      />
      <AddDialog open={adding} isAdmin={sales.isAdmin} leaders={leaders.filter((l) => l.active)} onClose={() => setAdding(false)} onSaved={saved} />

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Team;
